import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { graphql } from '@lib/api';
import { AGENT_BY_ID_QUERY } from '@lib/fragments';
import { useRouter } from 'next/router';
import { parseDateWithFormatMMMDDAndTime } from '@lib/utils';

// ---------------------------------------------------------

import Modal from '@components/modal/component';
import StepOne from './step-one';
import StepTwo from './step-two';

// ---------------------------------------------------------

import { container } from './styles.module.scss';

// ---------------------------------------------------------

const OpenHouseModal = (props) => {
  const { modalIsOpen, setModalIsOpen, listingUrl, agentId, listingId, openHouses, directionsUrl } =
    props;

  const router = useRouter();
  const [agent, setAgent] = useState();
  const [currentStep, setCurrentStep] = useState(1);

  const getUpcomingOpenHouse = () => {
    openHouses.map((openHouse) => {
      const formattedDateObject = parseDateWithFormatMMMDDAndTime(
        openHouse.formattedDateTimeDisplay
      );

      // Using endDate so that the open house data is displayed until the end time
      openHouse.date = formattedDateObject.endDate;
    });

    if (openHouses.length === 1) {
      return openHouses[0].formattedDateTimeDisplay;
    }

    const currrentOpenHouse = openHouses.find((openHouse) => {
      return new Date(openHouse.date) >= new Date();
    });

    return currrentOpenHouse.formattedDateTimeDisplay;
  };

  useEffect(() => {
    const fetchData = async () => {
      let variables = {
        agentId: parseInt(agentId)
      };

      const fetchedData = await graphql(
        gql`
          ${AGENT_BY_ID_QUERY}
        `,
        variables
      );
      setAgent(fetchedData?.agentSearch?.items[0] ?? []);
    };
    fetchData();
  }, [agent]);

  const onModalClose = () => {
    setModalIsOpen(false);
    setCurrentStep(1);

    router.replace(listingUrl, undefined, { shallow: true });
  };

  return (
    <Modal
      modalIsOpen={modalIsOpen}
      onModalClose={onModalClose}
      theme="pre-approval"
      content={
        <div className={container}>
          {currentStep === 1 && (
            <>
              <StepOne
                agent={agent}
                agentId={agentId}
                listingId={listingId}
                openHouse={getUpcomingOpenHouse()}
                nextStepCallback={() => setCurrentStep(2)}
              />
            </>
          )}
          {currentStep === 2 && (
            <StepTwo
              photos={props.photos}
              directionsUrl={directionsUrl}
              address={props.address}
              openHouse={getUpcomingOpenHouse()}
              setModalIsOpen={onModalClose}
            />
          )}
        </div>
      }
    />
  );
};

OpenHouseModal.propTypes = {
  /**
   * Specifies the listing address
   */
  address: PropTypes.string,

  /**
   * Specifies the id of the LDP agent card
   */
  agentId: PropTypes.number,

  /**
   * Specifies the listing address city
   */
  city: PropTypes.string,

  /**
   * Specifies the default value for the text area
   */
  defaultFormValue: PropTypes.string,

  /**
   * Specifies the request a tour listing ID
   */
  listingId: PropTypes.string,
  /**
   *
   * Specifies the request a tour listing URL
   */
  listingUrl: PropTypes.string,

  /**
   * Determines if the modal state is set to open.
   */
  modalIsOpen: PropTypes.bool,

  /**
   * Specifies the listing address property photos
   */
  photos: PropTypes.array,

  /**
   * Function to toggle modal state.
   */
  setModalIsOpen: PropTypes.func,

  /**
   * Specifies the listing address state
   */
  state: PropTypes.string,

  /**
   * Specifies the logged in user's information
   */
  userInfo: PropTypes.object,
  /**
   * Specifies the listing address zip code
   */
  zip: PropTypes.string,
  /**
   * Specifies the open house info
   */
  openHouses: PropTypes.array,
  /**
   * Specifies the directions URL
   */
  directionsUrl: PropTypes.string
};

export default OpenHouseModal;
